// Third-party packages
import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Container,
  Form,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Segment,
  Table,
  Checkbox,
  Input,
  Label,
} from 'semantic-ui-react'

// Main packages
import * as api from '../api.js'
import { AppContext } from './app_context.js'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

const PageAdmin = () => {
  const history = useHistory()
  const app = useContext(AppContext)
  const { customerInfo } = app || {}
  const [flags, setFlags] = useState({
    payment: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' },
    cart: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' },
    checkout: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' }
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('success')

  useEffect(() => {
    // Check authentication - admin must be logged in
    if (!api.isAuthenticated()) {
      history.push(`/login?from=${encodeURIComponent(window.location.pathname)}`)
      return
    }
    
    loadFlags()
  }, [history])

  const loadFlags = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/v1/flag-service/flags', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.flags) {
          setFlags(data.flags)
        }
      } else {
        setMessage('Failed to load chaos flags')
        setMessageType('error')
      }
    } catch (error) {
      console.error('Error loading flags:', error)
      setMessage('Error loading chaos flags')
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setMessage('')
      
      const response = await fetch('/api/v1/flag-service/flags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ flags }),
      })
      
      if (response.ok) {
        setMessage('Chaos flags updated successfully')
        setMessageType('success')
      } else {
        const errorData = await response.json()
        setMessage(errorData.error || 'Failed to update chaos flags')
        setMessageType('error')
      }
    } catch (error) {
      console.error('Error saving flags:', error)
      setMessage('Error saving chaos flags')
      setMessageType('error')
    } finally {
      setSaving(false)
    }
  }

  const handleServiceChange = (service, field, value) => {
    setFlags(prev => ({
      ...prev,
      [service]: {
        ...prev[service],
        [field]: field === 'enabled' ? value : 
                 field === 'delay_ms' ? parseInt(value) || 0 :
                 field === 'error_rate' ? parseFloat(value) || 0.0 :
                 value
      }
    }))
  }

  const handleReset = () => {
    setFlags({
      payment: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' },
      cart: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' },
      checkout: { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' }
    })
  }

  if (loading) {
    return (
      <>
        <SectionHeader />
        <Container style={{ marginTop: '2em' }}>
          <Loader active inline='centered' size='large'>Loading chaos flags...</Loader>
        </Container>
        <SectionFooter />
      </>
    )
  }

  const services = ['payment', 'cart', 'checkout']

  return (
    <>
      <SectionHeader />
      <Segment style={{ margin: 0, padding: '4em 0' }} vertical>
        <SectionTitle 
          title="Chaos Engineering Control Panel" 
          subtitle="Manage chaos flags for services"
        />
        <Container>
          {message && (
            <Message 
              positive={messageType === 'success'} 
              negative={messageType === 'error'}
              onDismiss={() => setMessage('')}
            >
              {message}
            </Message>
          )}

          <Form>
            <Grid stackable>
              {services.map(service => {
                const serviceFlags = flags[service] || { enabled: false, delay_ms: 0, error_rate: 0.0, mode: 'normal' }
                return (
                  <Grid.Row key={service}>
                    <Grid.Column width={16}>
                      <Segment>
                        <Header as='h3' style={{ textTransform: 'capitalize' }}>
                          {service} Service
                        </Header>
                        <Form.Field>
                          <Checkbox
                            toggle
                            label="Enable Chaos Engineering"
                            checked={serviceFlags.enabled}
                            onChange={(e, data) => handleServiceChange(service, 'enabled', data.checked)}
                          />
                        </Form.Field>
                        {serviceFlags.enabled && (
                          <>
                            <Form.Field>
                              <label>Delay (milliseconds)</label>
                              <Input
                                type="number"
                                min="0"
                                max="30000"
                                value={serviceFlags.delay_ms}
                                onChange={(e) => handleServiceChange(service, 'delay_ms', e.target.value)}
                                placeholder="0"
                              />
                            </Form.Field>
                            <Form.Field>
                              <label>Error Rate (0.0 - 1.0)</label>
                              <Input
                                type="number"
                                min="0"
                                max="1"
                                step="0.01"
                                value={serviceFlags.error_rate}
                                onChange={(e) => handleServiceChange(service, 'error_rate', e.target.value)}
                                placeholder="0.0"
                              />
                              <Label pointing="left">
                                {(serviceFlags.error_rate * 100).toFixed(0)}% error rate
                              </Label>
                            </Form.Field>
                            <Form.Field>
                              <label>Mode</label>
                              <Form.Select
                                options={[
                                  { key: 'normal', text: 'Normal', value: 'normal' },
                                  { key: 'chaos', text: 'Chaos', value: 'chaos' },
                                  { key: 'slow_responses', text: 'Slow Responses', value: 'slow_responses' }
                                ]}
                                value={serviceFlags.mode}
                                onChange={(e, data) => handleServiceChange(service, 'mode', data.value)}
                              />
                            </Form.Field>
                          </>
                        )}
                      </Segment>
                    </Grid.Column>
                  </Grid.Row>
                )
              })}
              <Grid.Row>
                <Grid.Column width={16}>
                  <Button.Group>
                    <Button 
                      positive 
                      onClick={handleSave} 
                      loading={saving}
                      disabled={saving}
                    >
                      <Icon name='save' />
                      Save Changes
                    </Button>
                    <Button.Or />
                    <Button 
                      onClick={handleReset}
                      disabled={saving}
                    >
                      <Icon name='refresh' />
                      Reset to Defaults
                    </Button>
                    <Button.Or />
                    <Button 
                      onClick={loadFlags}
                      disabled={saving}
                    >
                      <Icon name='reload' />
                      Reload
                    </Button>
                  </Button.Group>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Container>
      </Segment>
      <SectionFooter />
    </>
  )
}

export default PageAdmin

